import { DoqType, DoqWithSpeechBubble } from '@ardoq/doq';
import { DashboardApiError } from './types';
import { FlexBox } from '@ardoq/layout';
import { type I18nLocale, t } from '@ardoq/i18n';
import { ErrorState } from '@ardoq/status-ui';
import { type ArdoqError, toArdoqError } from '@ardoq/common-helpers';

interface DashboardErrorPageProps {
  error?: DashboardApiError;
  i18nLocale: I18nLocale;
}

const toDashboardArdoqError = (
  error: DashboardApiError | undefined,
  i18nLocale: I18nLocale
): ArdoqError => {
  // TODO: remove when unexpected errors are handled in dashboard reader
  //   |----------------------------------------------\
  if (error && error.message !== '' && error.traceId !== '') {
    return toArdoqError({
      error: new Error(error.message),
      userMessage: error.message,
      traceId: error.traceId,
    });
  }
  const genericMessage = t(
    i18nLocale,
    'dashboard:errorPage.genericMessage',
    'There is a technical issue with this dashboard. Please refresh and try again. If the problem continues, contact customer support'
  );
  return toArdoqError({
    error: new Error(genericMessage),
    userMessage: genericMessage,
  });
};

const DashboardErrorPage = ({ error, i18nLocale }: DashboardErrorPageProps) => {
  if (error?.code === 403) {
    return (
      <FlexBox justify="center" height="full" align="center">
        <DoqWithSpeechBubble
          title={t(
            i18nLocale,
            'dashboard:errorPage.noAccessTitle',
            'No access to Dashboard'
          )}
          doqType={DoqType.MISSING_ORG}
          message={t(
            i18nLocale,
            'dashboard:errorPage.noAccessMessage',
            "You don't have permissions to access this dashboard. To get access, please reach out to your Ardoq administrator."
          )}
        />
      </FlexBox>
    );
  }
  return (
    <FlexBox justify="center" height="full" align="center">
      <ErrorState
        layout="vertical"
        role="alert"
        title={t(
          i18nLocale,
          'dashboard:errorPage.genericTitle',
          'Oops, we hit a snag!'
        )}
        error={toDashboardArdoqError(error, i18nLocale)}
      />
    </FlexBox>
  );
};

export { DashboardErrorPage };
